import { Location } from 'history';
import isNumber from 'lodash-es/isNumber';
import { TAB_FORUMS, TAB_GROUPS } from 'mk/autogenerated/translations/GroupSubscribedFeedPage.1f83b0d281a5193b2a5d67db940ee857'
import { isAuthenticatedAs } from 'mk/bazaar/common/userUtils';
import { analyticsStoreEvent } from 'mk2/actions';
import LeftMenu from 'mk2/apps/blogs/components/LeftMenu';
import commentsSaga from 'mk2/apps/blogs/containers/Comments/Comments.sagas';
import { BLOG_POST_COMMENT_MENU_MORE, BLOG_POST_MENU_MORE } from 'mk2/apps/blogs/helpers';
import {
    BlogPostEntity,
    BlogPostSchema,
    GroupMemberEntity,
    GroupMemberSchema,
    UsersByGroupId,
} from 'mk2/apps/blogs/schemas';
import { PageRef } from 'mk2/apps/forum/schemas';
import { forumFeedUpdatedUrl } from 'mk2/apps/forum/urls';
import { parsePageNumber } from 'mk2/apps/forum/utils';
import { GROUP_COVER_MENU_MORE, GROUP_COVER_UPLOAD_FORM_NAME } from 'mk2/apps/groups/components/GroupCoverForm';
import {
    groupSharedDeleteGroupPostCommentInit,
    groupSharedDeleteGroupPostInit,
    groupSharedHidePostsOfAGroupInFeed,
    groupSharedInviteUser,
    groupSharedLeaveGroup,
    groupSharedPostSticky,
} from 'mk2/apps/groups/containers/GroupShared/GroupShared.actions';
import groupSharedReducer from 'mk2/apps/groups/containers/GroupShared/GroupShared.reducers';
import groupSharedSaga from 'mk2/apps/groups/containers/GroupShared/GroupShared.sagas';
import {
    getCommentComplaintsCounts,
    getModeratorUsersByGroupId,
    getPostsCommentsCount,
} from 'mk2/apps/groups/containers/GroupShared/GroupShared.selectors';
import { groupsSubscribedFeedUrl } from 'mk2/apps/groups/urls';
import { hintsCloseHint } from 'mk2/apps/users/containers/Hints/Hints.actions';
import { GROUP_SUBSCRIBED_FEED_HINT_NAME } from 'mk2/apps/users/containers/Hints/Hints.constants';
import hintsSaga from 'mk2/apps/users/containers/Hints/Hints.sagas';
import Btn, { BtnType } from 'mk2/components/Btn';
import { AppName } from 'mk2/containers/Application/Application';
import { complaintInitToggle } from 'mk2/containers/Complaint/Complaint.actions';
import { ComplaintTargetType } from 'mk2/containers/Complaint/Complaint.schemas';
import { getComplaintsShownForBlogPosts } from 'mk2/containers/Complaints/Complaints.selectors';
import { menuToggle } from 'mk2/containers/Menu/Menu.actions';
import { memoizeOne } from 'mk2/helpers/cache';
import { UserAgentParsed } from 'mk2/helpers/detects';
import { mkReduxFormMapDispatchToProps, mkReduxFormMapStateToProps } from 'mk2/helpers/form';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import FormPage, { FormPageDispatchProps, FormPageStateProps } from 'mk2/pages/FormPage';
import { BrandingProps, HalfpageProps, RightContentSide } from 'mk2/pages/Page';
import {
    AppState,
    getRequestBaseUrl,
    getRequestDeviceIsIOS,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
    getRequestUserAgent,
} from 'mk2/reducers';
import { LoadingState } from 'mk2/reducers/loadingState';
import { MenuSelection, UserEntity } from 'mk2/schemas';
import { getDenormalizedEntities } from 'mk2/selectors';
import { Store } from 'mk2/store/configureStore';
import { parse } from 'query-string';
import React from 'react';
import { MapStateToPropsParam } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import GroupSubscribedFeed from './GroupSubscribedFeed';
import { groupSubscribedFeedFetchTrigger, groupSubscribedFeedScrollToTrigger } from './GroupSubscribedFeed.actions';
import { GROUP_SUBSCRIBED_FEED_MENU_MORE, GROUP_SUBSCRIBED_FEED_MENU_ORDERBY } from './GroupSubscribedFeed.constants';
import reducer, {
    GroupSubscribedFeedPageState,
    GroupSubscribedFeedVirtualPageState,
    OrderBy,
} from './GroupSubscribedFeed.reducers';
import saga from './GroupSubscribedFeed.sagas';
import {
    getGroupSubscribedFeedPageState,
    getGroupSubscribedFeedSearchState,
    getGroupSubscribedFeedState,
    getGroupSubscribedFeedVirtualPageState,
} from './GroupSubscribedFeed.selectors';

export enum ScrollToPosition {
    FirstPost = 'first',
    LastPost = 'last',
}

interface OwnProps {}

interface StateProps extends FormPageStateProps {
    baseUrl: string;
    commentsCounts: { [postId: number]: number };
    complaintsCounts: { [postId: number]: number };
    complaintsOpened: boolean[];
    count: number;
    errorMessage: string;
    isIOS: boolean;
    loadingState: LoadingState;
    mobile: boolean;
    moderatorUsersByGroupId: UsersByGroupId;
    numberOfPages: number;
    orderBy: OrderBy;
    pageRef: PageRef;
    permissions: string[];
    posts: BlogPostEntity[];
    requestUser: UserEntity;
    showHint: boolean;
    userAgent: UserAgentParsed;
}

interface DispatchProps extends FormPageDispatchProps {
    onChangeSticky(postId: number, sticky: boolean);
    onCloseHint();
    onCommentDelete(commentId: number, key: string);
    onCommentToggleMenuMore(commentId: number);
    onDelete(postId: number);
    onGroupCoverToggleMenuMore();
    onGroupFeedMenuOrderBy();
    onGroupLeave(groupId: number, onSuccess);
    onGroupToggleMenuMore();
    onHideGroupInFeed(groupId: number, onSuccess);
    onInviteUser(groupId: number);
    onLoad(orderBy: OrderBy, pageRef: PageRef, currentUrl: Location, numCommentsBeforeLoad: number);
    onPostToggleMenuMore(postId: number);
    onReport(entity: ComplaintTargetType, id: number);
    onScrollTo(scrollto: ScrollToPosition);
    onStoreEvent(name: string, props: any);
}

type RouteProps = RouteComponentProps<{}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

interface Query {
    orderby: OrderBy;
}

export default class GroupSubscribedFeedPage extends FormPage<OwnProps & RouteProps, StateProps, DispatchProps> {

    private menuSelection = {
        activeSection: 'groups',
        activeItem: 'subscribed',
    };

    private parseQuery = memoizeOne((qs: string): Query => {
        const qp = parse(qs) || {};
        return {
            orderby: qp.orderby || 'posts',
        };
    });

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const parsedQuery: Query = this.parseQuery(ownProps.location.search);
            const orderBy = parsedQuery.orderby;

            const pageRef = parsePageNumber(ownProps.location);

            const groupSubscribedFeedState = getGroupSubscribedFeedState(state);
            const groupPostsState = getGroupSubscribedFeedSearchState(state, orderBy);

            const moderators = getDenormalizedEntities<GroupMemberEntity>(state, GroupMemberSchema, groupSubscribedFeedState.moderators);
            const moderatorUsers = moderators.map((groupMember) => groupMember.user);

            const permissions = getRequestPermissions(state);
            const requestUser = getRequestUser(state);
            const userAgent = getRequestUserAgent(state);

            const isAdmin = permissions.indexOf('forum.can_manage_forum') >= 0;
            const isModerator = moderatorUsers && isAuthenticatedAs(requestUser, moderatorUsers);

            const groupSubscribedFeedPageState: GroupSubscribedFeedPageState | GroupSubscribedFeedVirtualPageState = isNumber(pageRef)
                ? getGroupSubscribedFeedPageState(state, orderBy, pageRef)
                : getGroupSubscribedFeedVirtualPageState(state, orderBy, pageRef);

            const postIds = isNumber(pageRef)
                ? (groupSubscribedFeedPageState as GroupSubscribedFeedPageState).postIds
                : [];

            const posts = getDenormalizedEntities<BlogPostEntity>(state, BlogPostSchema, postIds);

            const commentsLoadingState = groupSubscribedFeedState.commentsLoadingState;

            const commentsCounts = getPostsCommentsCount(state, posts);

            const complaintsCounts = (isAdmin || isModerator)
                ? getCommentComplaintsCounts(state, postIds, commentsLoadingState)
                : {};

            return {
                ...mkReduxFormMapStateToProps(GROUP_COVER_UPLOAD_FORM_NAME)(state),
                baseUrl: getRequestBaseUrl(state),
                commentsCounts,
                complaintsCounts,
                complaintsOpened: getComplaintsShownForBlogPosts(state, posts),
                count: groupPostsState.count,
                errorMessage: groupSubscribedFeedPageState.errorMessage,
                isIOS: getRequestDeviceIsIOS(state),
                loadingState: groupSubscribedFeedPageState.loadingState,
                mobile: getRequestDeviceMobile(state),
                moderatorUsersByGroupId: getModeratorUsersByGroupId(moderators),
                numberOfPages: groupPostsState.numberOfPages,
                orderBy,
                pageRef,
                permissions,
                posts,
                requestUser,
                showHint: groupSubscribedFeedState.showHint,
                userAgent,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            ...mkReduxFormMapDispatchToProps(GROUP_COVER_UPLOAD_FORM_NAME),
            onChangeSticky: groupSharedPostSticky,
            onCloseHint: () => hintsCloseHint(GROUP_SUBSCRIBED_FEED_HINT_NAME),
            onCommentDelete: groupSharedDeleteGroupPostCommentInit,
            onCommentToggleMenuMore: (commentId: number) => menuToggle(BLOG_POST_COMMENT_MENU_MORE(commentId)),
            onDelete: groupSharedDeleteGroupPostInit,
            onGroupCoverToggleMenuMore: () => menuToggle(GROUP_COVER_MENU_MORE),
            onGroupFeedMenuOrderBy: () => menuToggle(GROUP_SUBSCRIBED_FEED_MENU_ORDERBY),
            onGroupLeave: (groupId, onSuccess) => groupSharedLeaveGroup(groupId, false, onSuccess),
            onHideGroupInFeed: groupSharedHidePostsOfAGroupInFeed,
            onGroupToggleMenuMore: () => menuToggle(GROUP_SUBSCRIBED_FEED_MENU_MORE),
            onInviteUser: groupSharedInviteUser,
            onLoad: groupSubscribedFeedFetchTrigger,
            onPostToggleMenuMore: (postId: number) => menuToggle(BLOG_POST_MENU_MORE(postId)),
            onReport: (entity: string, id: number) => complaintInitToggle(entity, id, true),
            onScrollTo: groupSubscribedFeedScrollToTrigger,
            onStoreEvent: analyticsStoreEvent,
        };
    }

    public getMetaTitle(props: Props): string {
        return null;    // TBD
    }

    public getMetaDescription(props: Props): string {
        return null;    // TBD
    }

    public render(props: Props): false | JSX.Element {
        return (
            <GroupSubscribedFeed {...props} />
        );
    }

    public renderRight(props: Props): JSX.Element {
        const { location } = props;
        return (
            <LeftMenu location={location} mergedForumAndGroups />
        );
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.LeftSide;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return false;
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: 'Groups Feed - Halfpage Sticky (Desktop)',
        };
    }

    public getBrandingProps(props: Props): BrandingProps {
        return {
            zone: 'Groups - Branding',
        };
    }

    public getHeaderTabs(props: Props): (JSX.Element[] | null) {
        return [(
            <Btn
                key="forums"
                type={BtnType.Tab}
                label={TAB_FORUMS}
                link={forumFeedUpdatedUrl}
            />
        ), (
            <Btn
                key="groups"
                type={BtnType.Tab}
                label={TAB_GROUPS}
                link={groupsSubscribedFeedUrl}
                active
            />
        )];
    }

    public getSubmitButton(props: Props): React.ReactNode {
        return null;
    }

    public getHeaderMenuSelection(app: AppName, props: Props): MenuSelection {
        return this.menuSelection;
    }

    public hasMergedForumAndGroups(props: Props): boolean {
        return true;
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer('containers.groups.groupSubscribedFeed', reducer);
        store.injectSaga('containers.groups.groupSubscribedFeed', saga);

        store.injectReducer('containers.groups.groupShared', groupSharedReducer);
        store.injectSaga('containers.groups.groupShared', groupSharedSaga);

        store.injectSaga('containers.blogs.shared.comments', commentsSaga);
        store.injectSaga('containers.users.hints', hintsSaga);
    }

}
